import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
    activeValidationsPersonaNatural
} from "../../redux/person/personSlice";

import {
    activeValidationsPersonaDocumento,
    setPersonaDocumentos,
    defaultPersonaDocumentos
} from "../../redux/person/personDocumentSlice";

import {
    setPersonaDirecciones,
    defaultPersonaDirecciones
} from "../../redux/person/personDirectionSlice";

import {
    setPersonaVinculaciones,
    defaultPersonaVinculaciones
} from "../../redux/person/personBondingSlice";

import {
    setPersonaContactos,
    defaultPersonaContactos
} from "../../redux/person/personContactSlice";

import * as personaService from "../../services/personaService";
import { puedeEditarPagina } from "../../services/permisoService";
import {
    notifySuccess,
    notifyWarning,
    notifyError
} from "../../services/notificationService";

export const usePersonNaturalForm = (
    sliceKey,
    options = {}) => {
    const dispatch = useDispatch();
    const {
        useBonding = true,
        usePast = false
    } = options;
    const EMPTY_ARRAY = [];

    const personaNatural = useSelector(
        state => state.person[sliceKey]?.personaNatural
    );

    const personaDirecciones = useSelector(
        state => state.personDirection[sliceKey]?.personaDirecciones
    );

    const personaDocumentos = useSelector(
        state => state.personDocument[sliceKey]?.personaDocumentos
    );

    const personaContactos = useSelector(
        state => state.personContact[sliceKey]?.personaContactos
    );

    const personaVinculaciones = useSelector(
        state => state.personBonding[sliceKey]?.personaVinculaciones
    );

    const personaNaturalValidation = useSelector(
        state => state.person[sliceKey]?.validationMessage ?? EMPTY_ARRAY
    );

    const personaDocumentosValidation = useSelector(
        state => state.personDocument[sliceKey]?.validationMessage ?? EMPTY_ARRAY
    );

    // La pagina solo se puede editar si el usuario tiene el permiso de Persona.
    const [isReadOnly, setIsReadOnly] = useState(!puedeEditarPagina("persona"));
    const [idNew, setIdNew] = useState(0);

    const buildPersonName = () => {
        return [
            personaNatural?.primerApellido,
            personaNatural?.segundoApellido,
            personaNatural?.primerNombre,
            personaNatural?.segundoNombre
        ]
            .map(s => (s || "").trim())
            .filter(s => s.length > 0 && s.toLowerCase() !== "null")
            .join(" ");
    };

    const buildPayload = () => ({
        ...personaNatural,
        nombres: buildPersonName(),
        idSexo: parseInt(personaNatural?.idSexo || 0),
        personaDireccion: personaDirecciones,
        personaDocumento: personaDocumentos,
        personaContacto: personaContactos,
        personaVinculacion: personaVinculaciones
    });

    const reloadDetails = async (personId) => {
        const directions =
            await personaService.getsPersonaDireccionView(personId);

        const contacts =
            await personaService.getsPersonaContactoView(personId);

        const documents =
            await personaService.getsPersonaDocumentoView(personId);

        dispatch(setPersonaDirecciones({
            sliceKey,
            data: directions?.length
                ? directions
                : defaultPersonaDirecciones
        }));

        dispatch(setPersonaContactos({
            sliceKey,
            data: contacts?.length
                ? contacts
                : defaultPersonaContactos
        }));

        dispatch(setPersonaDocumentos({
            sliceKey,
            data: documents?.length
                ? documents
                : defaultPersonaDocumentos
        }));

        if (useBonding) {
            const bondings =
                await personaService.getsPersonaVinculacionView(personId);

            dispatch(setPersonaVinculaciones({
                sliceKey,
                data: bondings?.length
                    ? bondings
                    : defaultPersonaVinculaciones
            }));
        }
    };

    const savePerson = async () => {
        const validationMessages =
            personaNaturalValidation.concat(personaDocumentosValidation);

        if (validationMessages.length > 0) {
            dispatch(activeValidationsPersonaNatural({ sliceKey }));
            dispatch(activeValidationsPersonaDocumento({ sliceKey }));

            notifyWarning(validationMessages.join(" - "));
            return null;
        }

        try {
            const dataPost = buildPayload();

            const response =
                await personaService.postPersonaNaturalUpdateWithDetails(dataPost);

            const newId = parseInt(response.data.data);

            notifySuccess("Se guardaron los datos con éxito");

            setIdNew(newId);
            setIsReadOnly(true);

            if (personaNatural?.id > 0) {
                await reloadDetails(personaNatural.id);
            }

            return {
                ...dataPost,
                id: newId,
            };
        }
        catch (error) {
            notifyError(
                error.response?.data?.message ??
                error.message ??
                "An unexpected error occurred."
            );

            return null;
        }
    };

    return {
        personaNatural,
        isReadOnly,
        setIsReadOnly,
        idNew,
        savePerson
    };
};